import { api } from './api'

interface LoginRequest {
  email: string
  password: string
}

interface AuthUser {
  id: string
  email: string
  name: string
  role: string
}

/**
 * Auth utilities.
 * JWT tokens are stored in httpOnly cookies by the API.
 * The frontend never directly handles tokens.
 */

export async function login(credentials: LoginRequest): Promise<AuthUser> {
  // API returns { user: { id: number, email, display_name, role } }
  const response = await api<{ user: { id: number; email: string; display_name: string; role: string } }>('/v1/auth/login', {
    method: 'POST',
    body: credentials,
  })
  return {
    id: String(response.user.id),
    email: response.user.email,
    name: response.user.display_name,
    role: response.user.role,
  }
}

// Token refresh is handled transparently by the API middleware via cookies.
// No explicit refresh endpoint is needed.

export async function logout(): Promise<void> {
  return api('/v1/auth/logout', { method: 'POST' })
}

export async function getCurrentUser(): Promise<AuthUser> {
  // API returns { id: number, email, role, permissions } — no name field
  const response = await api<{ id: number; email: string; role: string; name?: string }>('/v1/auth/me')
  return {
    id: String(response.id),
    email: response.email,
    name: response.name ?? response.email.split('@')[0],
    role: response.role,
  }
}

// ============================================================================
// MFA
// ============================================================================

export interface MFASetupResponse {
  secret: string
  qr_code_url: string
  recovery_codes: string[]
}

export interface MFAVerifyResponse {
  enabled: boolean
  message: string
}

export interface MFARecoveryResponse {
  message: string
  codes_remaining: number
}

/** Initiate MFA setup - returns TOTP secret, QR code URL, and recovery codes. */
export async function mfaSetup(): Promise<MFASetupResponse> {
  return api<MFASetupResponse>('/v1/auth/mfa/setup', { method: 'POST' })
}

/** Verify a TOTP code (used during setup to enable MFA, and during login as 2FA). */
export async function mfaVerify(code: string): Promise<MFAVerifyResponse> {
  return api<MFAVerifyResponse>('/v1/auth/mfa/verify', {
    method: 'POST',
    body: { code },
  })
}

/** Verify a recovery code when TOTP device is unavailable. */
export async function mfaRecovery(recoveryCode: string): Promise<MFARecoveryResponse> {
  return api<MFARecoveryResponse>('/v1/auth/mfa/recovery', {
    method: 'POST',
    body: { recovery_code: recoveryCode },
  })
}
